import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SampleTypeEnum, getSelectableSampleTypes } from 'src/app/enums/sampleType.enum';
import { TenantRisk } from 'src/app/models/tenantRisk.model';
import {
    SampleRequiredFieldOption,
    SampleValidationConfig,
    SampleValidationRule,
    SampleValidationRuleSection,
    SampleValidationRuleSectionErrors
} from 'src/app/models/sampleValidationConfig.model';
import { TenantRiskService } from 'src/app/services/tenantRisk.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import {
    MAX_NOISE_DRIFT_DB,
    MAX_RUN_TIME_MINUTES,
    SAMPLE_REQUIRED_FIELD_OPTIONS,
    SAMPLE_VALIDATION_DEFAULTS,
    SECONDS_PER_MINUTE
} from 'src/app/shared/constants/sampleValidationRules.constants';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridPermissions } from 'src/app/shared/grid/core-grid/gridPermissions.enum';

@Component({
  standalone: false,
    selector: 'app-sample-validation-rules-form',
    templateUrl: './sample-validation-rules-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./sample-validation-rules-form.component.scss'],
})
export class SampleValidationRulesFormComponent implements OnInit, OnDestroy {
    sections: SampleValidationRuleSection[] = [];
    // Permission gating for the mutating controls (Save, per-section Reset). These persist to
    // TenantRisk, so gate on RolePermissionObjects.TenantRisk with Update. See DASH-3101.
    readonly tenantRiskPermissionObject = RolePermissionObjects.TenantRisk;
    readonly updateAction = GridPermissions.Update;
    readonly requiredFieldOptions: SampleRequiredFieldOption[] = SAMPLE_REQUIRED_FIELD_OPTIONS;
    readonly defaultCalDriftPercent = SAMPLE_VALIDATION_DEFAULTS.maxCalDriftPercent * 100;
    readonly defaultNoiseDriftDb = SAMPLE_VALIDATION_DEFAULTS.maxNoiseDriftDb;

    isLoading = false;
    hasChanges = false;
    hasErrors = false;

    private tenantRisk: TenantRisk | null = null;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly tenantRiskService: TenantRiskService,
        private readonly toastService: ToastService,
        private readonly translateService: TranslateService
    ) { }

    ngOnInit(): void {
        this.loadConfiguration();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadConfiguration(): void {
        this.isLoading = true;
        this.tenantRiskService.getCurrent().pipe(takeUntil(this.destroy$)).subscribe({
            next: (tenantRisk) => {
                this.tenantRisk = tenantRisk;
                this.buildSections(this.normalizeSampleTypeRules(tenantRisk?.sampleValidationConfig?.sampleTypeRules));
                this.hasChanges = false;
                this.isLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
            }
        });
    }

    /**
     * DASH-2714: coerce a loaded sampleTypeRules map to a single canonical key format. Older builds
     * persisted string enum-name keys ("Air", "Noise") alongside the BE's integer keys ("0", "1"),
     * so the same sample type could appear twice. This resolves both forms to the numeric
     * SampleTypeEnum key and de-duplicates.
     *
     * Dedup is DETERMINISTIC and canonical-key-authoritative: when a type is present under BOTH a
     * numeric key ("0") and a legacy string key ("Air"), the numeric entry always wins. The backend
     * (SampleTypeRulesJsonConverter) always persists/emits the canonical integer key, so making the
     * numeric key authoritative here can never let a stale legacy string value clobber the canonical
     * BE-written value. We deliberately do NOT depend on key enumeration order: Angular's HttpClient
     * has already JSON.parse'd the response and JS hoists integer-like keys ahead of string keys, so
     * the original JSON document order that the BE's Utf8JsonReader honours is not recoverable here.
     * (In the paired deployment the BE also de-dups on read, so the FE normally only ever receives
     * single numeric-keyed entries; this pass is the transitional/defensive guard.)
     *
     * Unknown numeric keys (future BE-only sample types) are preserved; unknown non-numeric keys
     * are dropped so no ambiguous entry is ever persisted.
     */
    private normalizeSampleTypeRules(
        rules: { [key: string]: SampleValidationRule | undefined } | null | undefined
    ): { [key in SampleTypeEnum]?: SampleValidationRule } {
        const normalized: { [key in SampleTypeEnum]?: SampleValidationRule } = {};
        if (!rules) {
            return normalized;
        }
        const numericKeyed = new Set<SampleTypeEnum>();
        for (const rawKey of Object.keys(rules)) {
            const rule = rules[rawKey];
            if (rule == null) {
                continue;
            }
            const isNumericKey = rawKey.trim() !== '' && !Number.isNaN(Number(rawKey));
            const sampleType = isNumericKey
                ? Number(rawKey) as SampleTypeEnum
                : SampleTypeEnum[rawKey as keyof typeof SampleTypeEnum] as SampleTypeEnum | undefined;
            if (sampleType == null) {
                continue;
            }
            if (isNumericKey) {
                // Canonical numeric key: always authoritative, overwrites any legacy string entry.
                normalized[sampleType] = rule;
                numericKeyed.add(sampleType);
            } else if (!numericKeyed.has(sampleType)) {
                // Legacy string enum-name key: used only as a fallback when no numeric key exists
                // for this type, so the canonical value wins regardless of enumeration order.
                normalized[sampleType] = rule;
            }
        }
        return normalized;
    }

    private buildSections(rules: { [key in SampleTypeEnum]?: SampleValidationRule }): void {
        // Only render acceptance-rule sections for sample types that can actually have samples.
        // Agent-type-only classifications (Other, Vibration) are gated out — they cannot
        // carry samples, so a Max Cal Drift % rule is meaningless for them. Any existing rule for a
        // gated type survives the round-trip: saveConfiguration seeds from the loaded config and only
        // overwrites/deletes types it renders as a section.
        this.sections = getSelectableSampleTypes().map(({ id, name }) => {
            const sampleType = id as SampleTypeEnum;
            const rule = rules[sampleType] ?? {};
            const usesNoiseDrift = sampleType === SampleTypeEnum.Noise;
            const minRunTimeMinutes = rule.minRunTimeSeconds != null
                ? rule.minRunTimeSeconds / SECONDS_PER_MINUTE
                : null;

            const section: SampleValidationRuleSection = {
                sampleType,
                label: this.translateService.getDynamicTranslation(name),
                usesNoiseDrift,
                maxCalDriftPercent: rule.maxCalDriftPercent != null ? rule.maxCalDriftPercent * 100 : null,
                maxNoiseDriftDb: rule.maxNoiseDriftDb ?? null,
                minRunTimeMinutes,
                requiredFields: [...(rule.requiredFields ?? [])],
                isCustom: false,
                runTimeSecondsHelper: '',
                errors: {}
            };
            this.refreshSection(section);
            return section;
        });
        this.updateHasErrors();
    }

    private refreshSection(section: SampleValidationRuleSection): void {
        section.runTimeSecondsHelper = section.minRunTimeMinutes != null && section.minRunTimeMinutes >= 0
            ? this.translateService.getDynamicTranslation('= {{seconds}} sec', {
                seconds: Math.round(section.minRunTimeMinutes * SECONDS_PER_MINUTE)
            })
            : '';
        section.isCustom = this.isSectionCustom(section);
        section.errors = this.validateSection(section);
    }

    private validateSection(section: SampleValidationRuleSection): SampleValidationRuleSectionErrors {
        const errors: SampleValidationRuleSectionErrors = {};
        if (!section.usesNoiseDrift && section.maxCalDriftPercent != null
            && (section.maxCalDriftPercent <= 0 || section.maxCalDriftPercent > 100)) {
            errors.maxCalDriftPercent = 'Enter a percent greater than 0 and up to 100';
        }
        if (section.usesNoiseDrift && section.maxNoiseDriftDb != null
            && (section.maxNoiseDriftDb < 0 || section.maxNoiseDriftDb > MAX_NOISE_DRIFT_DB)) {
            errors.maxNoiseDriftDb = 'Enter a value between 0 and 200 dB';
        }
        if (section.minRunTimeMinutes != null
            && (section.minRunTimeMinutes < 0 || section.minRunTimeMinutes > MAX_RUN_TIME_MINUTES)) {
            errors.minRunTimeMinutes = 'Enter a value between 0 and 1440 minutes';
        }
        return errors;
    }

    private updateHasErrors(): void {
        this.hasErrors = this.sections.some(s => Object.keys(s.errors).length > 0);
    }

    private isSectionCustom(section: SampleValidationRuleSection): boolean {
        const drift = section.usesNoiseDrift ? section.maxNoiseDriftDb : section.maxCalDriftPercent;
        return drift != null || section.minRunTimeMinutes != null || section.requiredFields.length > 0;
    }

    onSectionChange(section: SampleValidationRuleSection): void {
        this.refreshSection(section);
        this.updateHasErrors();
        this.hasChanges = true;
    }

    saveConfiguration(): void {
        if (!this.tenantRisk || this.hasErrors) {
            return;
        }
        this.isLoading = true;

        // Start from the loaded config so any sampleTypeRules key we don't render as a section
        // (e.g. a future BE-only SampleTypeEnum value) survives the round-trip instead of being dropped.
        // Normalise first so legacy string enum-name keys ("Air") collapse into the canonical numeric
        // keys ("0") and never accumulate as duplicate entries for the same sample type (DASH-2714).
        const existingRules = this.normalizeSampleTypeRules(this.tenantRisk.sampleValidationConfig?.sampleTypeRules);
        const sampleTypeRules: { [key in SampleTypeEnum]?: SampleValidationRule } = { ...existingRules };
        for (const section of this.sections) {
            const rule = this.toRule(section);
            if (rule) {
                sampleTypeRules[section.sampleType] = rule;
            } else {
                delete sampleTypeRules[section.sampleType];
            }
        }

        const sampleValidationConfig: SampleValidationConfig = { sampleTypeRules };

        // G7 is the first FE writer of TenantRisk config: spread the loaded object and set
        // ONLY sampleValidationConfig so riskLevels / Bayesian JSON fields survive the round-trip.
        const payload: TenantRisk = { ...this.tenantRisk, sampleValidationConfig };

        this.tenantRiskService.update(payload).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.toastService.showSuccess('Sample acceptance rules saved successfully', true);
                this.tenantRisk = payload;
                this.hasChanges = false;
                this.isLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
            }
        });
    }

    private toRule(section: SampleValidationRuleSection): SampleValidationRule | null {
        if (!this.isSectionCustom(section)) {
            return null;
        }
        return {
            maxCalDriftPercent: !section.usesNoiseDrift && section.maxCalDriftPercent != null
                ? section.maxCalDriftPercent / 100
                : null,
            maxNoiseDriftDb: section.usesNoiseDrift ? section.maxNoiseDriftDb : null,
            minRunTimeSeconds: section.minRunTimeMinutes != null
                ? Math.round(section.minRunTimeMinutes * SECONDS_PER_MINUTE)
                : null,
            requiredFields: section.requiredFields.length > 0 ? [...section.requiredFields] : null
        };
    }

    resetSection(section: SampleValidationRuleSection): void {
        section.maxCalDriftPercent = null;
        section.maxNoiseDriftDb = null;
        section.minRunTimeMinutes = null;
        section.requiredFields = [];
        this.onSectionChange(section);
    }

    trackBySampleType = (_: number, section: SampleValidationRuleSection): SampleTypeEnum => section.sampleType;
    trackByIndex = trackByIndex;
}
